import * as Yup from 'yup';
import { API_BASE_URL } from '@synergy/config';
import { Address, DocumentStatus, Locale } from '@synergy/models';
import { ArrayHelpers, Form, Formik, FormikProps, getIn } from 'formik';
import { Box, IconButton, Theme } from '@mui/material';
import { DeclineIcon } from '@synergy/theme';
import { FormattedMessage } from 'react-intl';
import { MouseEvent } from 'react';
import { PaloDescriptionModeToggle, PaloFormGrid, PaloJumpBar, PaloRecordCard } from '@synergy/components-palo';
import { PaloNextThemeProvider } from '@synergy/theme-palo';
import { PoolButton } from '@synergy/components-next';
import { ScrollToFieldError } from '@synergy/components';
import { WithStyles, createStyles, withStyles } from '@mui/styles';
import { connect } from 'react-redux';
import { getCurrencySymbol } from '@synergy/utils';
import AddIcon from '@mui/icons-material/AddCircleOutline';

import { AccountApi } from '../../../common/api/account-api';
import { AccountPurchase } from '../models/account-purchase';
import { AppState } from '../../../store/models/app-state';
import { Contact } from '../../../common/models/contact';
import { CustomPosition } from '../../../common/components/free-position-dialog/model/custom-position';
import { CustomPositionFields } from '../../../common/components/free-position-dialog/model/custom-position-fields';
import { Customer } from '../../../common/models/customer';
import {
    FormControlledStepComponent,
    FormControlledStepComponentProps,
} from '../../../common/components/abstract/form-controlled-step-component';
import { FreePositionDialog } from '../../../common/components/free-position-dialog/free-position-dialog';
import { Inquiry } from '../models/inquiry';
import { InquiryDetailPositionsArticles } from './inquiry-detail-positions-articles';
import { PaloCustomerQuickSearch } from '../../../common/components/palo/palo-customer-quick-search';
import { PaloDeliveryDateControl } from '../../../common/components/palo/palo-delivery-date-control';
import { PaloDirtyComparison } from '../../../common/components/palo/palo-dirty-context';
import { PaloFormikField } from '../../../common/components/palo/palo-formik-field';
import { PaloInquiryNotesWatcher } from '../palo/palo-inquiry-notes-watcher';
import { PaloTextControl } from '../../../common/components/palo/palo-text-control';
import {
    PartialAccountContact,
    PoolAccountContactAutocompleteList,
} from '../../../common/components/autocomplete-list/account-contact-autocomplete-list/pool-account-contact-autocomplete-list';
import {
    PartialAddress,
    PoolAccountAddressAutocompleteList,
} from '../../../common/components/autocomplete-list/account-address-autocomplete-list/pool-account-address-autocomplete-list';
import { PoolCustomerAutocompleteList } from '../../../common/components/autocomplete-list/customer-autocomplete-list/pool-customer-autocomplete-list';
import { PurchaseItem } from '../models/purchase-item';
import { User } from '../../../common/models/user';
import { bestLocaleSelector } from '../../../common/selectors/locale-selectors';
import { customerChipLabel, customerLabel } from '../utils/customer-label';
import {
    isBlankPosition,
    withFirstPosition,
    withoutBlankPositions,
} from '../../../common/components/palo/palo-first-position';
import { onlyOne } from '../../../common/utils/only-one';

/** The blank row "Zeile hinzufügen" adds in a customer card. */
const blankPurchaseItem = (): PurchaseItem => PurchaseItem.fromPlainObject({ name: {}, orderUnit: '', quantity: 1 });

/** A customer card with other rows, without touching the loaded document. */
const withItems = (accountPurchase: AccountPurchase, purchaseItems: PurchaseItem[]): AccountPurchase =>
    Object.assign(Object.create(Object.getPrototypeOf(accountPurchase)), accountPurchase, { purchaseItems });

const styles = (theme: Theme) =>
    createStyles({
        container: {
            display: 'flex',
        },
        content: {
            flex: 1,
            minWidth: 0,
        },
    });

interface InquiryDetailPositionsProps
    extends WithStyles<typeof styles>,
        FormControlledStepComponentProps<Partial<Inquiry>> {
    currency: string;
    currencySymbol: string;
    locale: Locale;
    appLocale: Locale;
    user: User;
    mediaBaseUrl: string;
}

interface CustomPositionData {
    index: number;
    wasNormArticle: boolean;
}

interface InquiryDetailPositionsState {
    productSearchFieldIndex: number;
    /** Cards opened or closed by hand, by customer index; the rest follow the default. */
    openCards: Record<number, boolean>;
    /** The customer added last, which stays open among many. */
    lastAddedIndex?: number;
    customPositionModalOpen: boolean;
    customData?: CustomPositionData;
    customProduct?: CustomPosition;
}

export interface InquiryDetailPositionsFormValues {
    accountPurchases: AccountPurchase[];
}

/**
 * A customer card added but left empty: no customer, no position, never
 * saved. It is not saved now either, so it is not checked — it turned red
 * and held up the save as soon as another card had a customer (#2308).
 * A saved card is never blank: dropping it would drop its offers. Nor is
 * one with a date: the date can be set before the customer (R-220), and
 * dropping the card dropped it without a word — it now asks for the rest.
 */
const isBlankCard = (accountPurchase: AccountPurchase): boolean =>
    !accountPurchase.id &&
    !accountPurchase.customerAccount &&
    !accountPurchase.deliveryDate &&
    0 === withoutBlankPositions(accountPurchase.purchaseItems ?? []).length;

/** One customer of the inquiry: who and where, then the people and references there. */
const CUSTOMER_CARD_AREAS = ['customer customer deliveryAddress deliveryAddress', 'contact aviso costCentre reference'];

/** The same card in three columns, for a narrow mask. */
const CUSTOMER_CARD_NARROW_AREAS = [
    'customer customer contact',
    'deliveryAddress deliveryAddress aviso',
    'costCentre reference .',
];

class InquiryDetailPositionsBase extends FormControlledStepComponent<
    Partial<Inquiry>,
    InquiryDetailPositionsFormValues,
    InquiryDetailPositionsProps,
    InquiryDetailPositionsState
> {
    private api = new AccountApi(API_BASE_URL);

    /** The customer whose lists are being read, per position. */
    private lastCustomerIdByPath: Record<string, number | undefined> = {};

    public state: InquiryDetailPositionsState = {
        openCards: {},
        productSearchFieldIndex: 0,
        customPositionModalOpen: false,
        customProduct: {
            name: { de: '', en: '', de_ch: '', fr: '' },
            orderUnit: '',
            isNormArticle: false,
            productNumber: '',
        },
    };

    protected initialValues: InquiryDetailPositionsFormValues = {
        accountPurchases: [],
    };

    protected customerName = '';
    protected formikArrayHelper: ArrayHelpers | null = null;

    protected onSubmit = (values: InquiryDetailPositionsFormValues) => {
        // A blank row is no position and is not handed on (#2308).
        this.props.propagateInternalState({
            accountPurchases: values.accountPurchases
                .filter(accountPurchase => !isBlankCard(accountPurchase))
                .map(accountPurchase =>
                    withItems(accountPurchase, withoutBlankPositions(accountPurchase.purchaseItems ?? []))
                ),
        });

        if (this.formikInstance) {
            this.formikInstance.setFormikState(prevState => ({ ...prevState, submitCount: 0 }));
        }
    };

    private handleAddCustomPosition = (value: CustomPosition, data?: unknown) => {
        if (this.formikArrayHelper) {
            const purchaseItem = PurchaseItem.fromPlainObject({
                ...value,
                currency: this.props.currency,
                isBestPrice: false,
                quantity: value.quantity,
            });

            const customPositionData = data as CustomPositionData;
            if (customPositionData?.index !== undefined) {
                if (customPositionData.wasNormArticle !== value.isNormArticle) {
                    purchaseItem.id = undefined;
                }
                this.formikArrayHelper.replace(customPositionData.index, purchaseItem);
            } else {
                this.formikArrayHelper.push(purchaseItem);
            }

            this.closeAddCustomPositionModal();
        }
    };

    private openAddCustomPositionModal = (helper: ArrayHelpers, index?: number, purchaseItem?: PurchaseItem) => {
        this.formikArrayHelper = helper;
        this.setState({
            customPositionModalOpen: true,
            customProduct: purchaseItem,
            customData:
                (index !== undefined &&
                    purchaseItem && {
                        index,
                        wasNormArticle: purchaseItem.isNormArticle || false,
                    }) ||
                undefined,
        });
    };

    private closeAddCustomPositionModal = () => {
        this.formikArrayHelper = null;
        this.setState({
            customPositionModalOpen: false,
        });
    };

    private getCurrentCustomerId(
        formikBag: FormikProps<InquiryDetailPositionsFormValues>,
        accountPurchaseIndex: number
    ) {
        const customerAccount =
            formikBag.values.accountPurchases[accountPurchaseIndex] &&
            formikBag.values.accountPurchases[accountPurchaseIndex].customerAccount;
        return customerAccount && customerAccount.id;
    }

    private handleCreateCustomer = () => {
        const accountPurchase = new AccountPurchase();
        accountPurchase.purchaseItems = [blankPurchaseItem()];

        if (this.formikInstance) {
            const accountPurchases = this.formikInstance.values.accountPurchases as AccountPurchase[];
            this.formikInstance.setValues({ accountPurchases: [...accountPurchases, accountPurchase] });
            this.setState({ lastAddedIndex: accountPurchases.length });
        }
    };

    private handleRemoveCustomer = (accountPurchaseIndex: number) => (event: MouseEvent<HTMLElement>) => {
        event.stopPropagation();

        if (this.formikInstance) {
            const accountPurchases = (this.formikInstance.values.accountPurchases as AccountPurchase[]).filter(
                (_, index) => index !== accountPurchaseIndex
            );
            this.formikInstance.setValues({ accountPurchases });
            // The indexes behind the removed customer move up by one.
            this.setState({ openCards: {}, lastAddedIndex: undefined });
        }
    };

    private isReadonly(): boolean {
        const status = this.props.globalState?.status;

        return !this.props.unlocked && !!status && status !== DocumentStatus.Created;
    }

    private toFormValues(state?: Partial<Inquiry>): InquiryDetailPositionsFormValues {
        // Copied, so that the placeholder below and the array helpers cannot
        // reach through into the loaded document — which is what the unsaved
        // marker compares against.
        const accountPurchases = [...(state?.accountPurchases ?? [])];

        if (accountPurchases.length === 0) {
            const accountPurchase = new AccountPurchase();
            accountPurchase.purchaseItems = [];
            accountPurchases.push(accountPurchase);
        }

        // Every customer card brings its first position while it has none.
        // The form and the document compared against both pass through here,
        // so the blank row is no unsaved input (#2308).
        const editable = !this.isReadonly();

        return {
            ...this.initialValues,
            accountPurchases: accountPurchases.map(accountPurchase => {
                const items = accountPurchase.purchaseItems ?? [];
                const shown = withFirstPosition(items, editable, blankPurchaseItem);

                return shown === items ? accountPurchase : withItems(accountPurchase, shown);
            }),
        };
    }

    private updateInternalValuesByProps(props: InquiryDetailPositionsProps) {
        if (!this.formikInstance) {
            return;
        }

        this.formikInstance.setValues(this.toFormValues(props.internalState));
    }

    private handleDeliveryAddressSelected =
        (formikBag: FormikProps<InquiryDetailPositionsFormValues>, pathPrefix: string) => (address: Address | null) => {
            formikBag.setFieldValue(`${pathPrefix}.deliveryAddress`, address);
            if (address && address.costCentre) {
                formikBag.setFieldValue(`${pathPrefix}.costCentre`, address.costCentre);
            }
        };

    private handleCustomerSelected =
        (formikBag: FormikProps<InquiryDetailPositionsFormValues>, pathPrefix: string) => (value: Customer | null) => {
            formikBag.setFieldValue(`${pathPrefix}.customerAccount`, value);
            formikBag.setFieldValue(`${pathPrefix}.deliveryAddress`, null);
            formikBag.setFieldValue(`${pathPrefix}.contact`, null);
            formikBag.setFieldValue(`${pathPrefix}.avisoContact`, null);

            void this.fillWhatIsUnambiguous(formikBag, pathPrefix, value, {});
        };

    /**
     * Fills the two required fields the customer leaves no choice about.
     *
     * A customer with exactly one delivery address and one contact still left
     * both empty, and the order does this since #2210 (R-14) — the inquiry is
     * the same form one step earlier (#2214, R-37). "Aviso an" stays empty:
     * it is not required, and whom to notify is a decision, not a lookup.
     */
    private fillWhatIsUnambiguous = async (
        formikBag: FormikProps<InquiryDetailPositionsFormValues>,
        pathPrefix: string,
        customer: Customer | null,
        filled: { contact?: Contact; deliveryAddress?: Address }
    ) => {
        this.lastCustomerIdByPath[pathPrefix] = customer?.id;

        if (!customer?.id) {
            return;
        }

        const [addresses, contacts] = await Promise.all([
            filled.deliveryAddress ? undefined : this.api.getAddresses(customer.id, undefined, false),
            filled.contact ? undefined : this.api.getContacts(customer.id),
        ]);

        // The customer of this position may have been changed again while this
        // was on the way; the bag carries the values of the render it was made
        // in, so only our own note tells.
        if (this.lastCustomerIdByPath[pathPrefix] !== customer.id) {
            return;
        }

        const onlyContact = contacts?.success ? onlyOne(contacts.result) : undefined;
        if (onlyContact) {
            formikBag.setFieldValue(`${pathPrefix}.contact`, onlyContact);
        }

        const onlyDelivery = addresses?.success
            ? onlyOne(addresses.result.filter(candidate => candidate.isDeliveryAddress))
            : undefined;
        if (onlyDelivery) {
            formikBag.setFieldValue(`${pathPrefix}.deliveryAddress`, onlyDelivery);

            if (onlyDelivery.costCentre) {
                formikBag.setFieldValue(`${pathPrefix}.costCentre`, onlyDelivery.costCentre);
            }
        }
    };

    private handleQuickSearchSelected =
        (formikBag: FormikProps<InquiryDetailPositionsFormValues>, pathPrefix: string) =>
        (customer: Customer, contact?: Contact, address?: Address) => {
            formikBag.setFieldValue(`${pathPrefix}.customerAccount`, customer);
            formikBag.setFieldValue(
                `${pathPrefix}.deliveryAddress`,
                address && address.isDeliveryAddress ? address : null
            );
            formikBag.setFieldValue(`${pathPrefix}.contact`, contact);
            formikBag.setFieldValue(`${pathPrefix}.avisoContact`, null);
            if (address && address.costCentre) {
                formikBag.setFieldValue(`${pathPrefix}.costCentre`, address.costCentre);
            }

            void this.fillWhatIsUnambiguous(formikBag, pathPrefix, customer, {
                contact,
                deliveryAddress: address?.isDeliveryAddress ? address : undefined,
            });
        };

    public componentDidMount() {
        this.updateInternalValuesByProps(this.props);
    }

    public UNSAFE_componentWillReceiveProps(nextProps: InquiryDetailPositionsProps) {
        if (this.props.internalState !== nextProps.internalState) {
            this.updateInternalValuesByProps(nextProps);
        }
    }

    /**
     * Up to two customers every card is open. From three on they start closed so
     * the page stays crossable, except the one added last; a card with a field
     * that failed to save opens by itself (#2252).
     */
    /**
     * Nothing entered for any customer yet. Overview and positions share a tab
     * now (#2252), and saving checks every step of the tab — which would have
     * refused a draft saved with its head alone, as it always could be. Until a
     * customer or a position is entered, this step takes no part in saving.
     */
    private isUntouched(): boolean {
        // The same rule as for a card that is dropped: one holding only a date
        // left this step out of saving and went without a word (R-220).
        return (this.formikInstance?.values.accountPurchases ?? []).every(isBlankCard);
    }

    protected override tryToActivate = async () => {
        if (this.formikInstance && !this.isUntouched()) {
            await this.formikInstance.submitForm();
        }
    };

    protected override canActivate = () => !this.formikInstance || this.isUntouched() || this.formikInstance.isValid;

    private isCardOpen(formikBag: FormikProps<InquiryDetailPositionsFormValues>, index: number): boolean {
        const count = formikBag.values.accountPurchases.length;

        if (formikBag.submitCount > 0 && getIn(formikBag.errors, `accountPurchases.${index}`)) {
            return true;
        }

        return this.state.openCards[index] ?? (count <= 2 || index === (this.state.lastAddedIndex ?? count - 1));
    }

    private toggleCard = (formikBag: FormikProps<InquiryDetailPositionsFormValues>, index: number) => () =>
        this.setState(state => ({ openCards: { ...state.openCards, [index]: !this.isCardOpen(formikBag, index) } }));

    private jumpToCard = (index: number) => () => {
        this.setState(state => ({ openCards: { ...state.openCards, [index]: true } }));
        requestAnimationFrame(() =>
            document.getElementById(`inquiry-customer-${index}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
        );
    };

    private renderCustomers(formikBag: FormikProps<InquiryDetailPositionsFormValues>, readonly: boolean) {
        const { accountPurchases } = formikBag.values;

        return (
            <>
                <PaloJumpBar
                    title={<FormattedMessage id="app.inquiries.detail.customers" />}
                    items={accountPurchases.map((accountPurchase, index) => ({
                        key: index,
                        label: customerChipLabel(accountPurchase, accountPurchases) ?? (
                            <FormattedMessage id="app.inquiries.detail.customerNotChosen" />
                        ),
                        meta: accountPurchase.purchaseItems?.length ?? 0,
                        onClick: this.jumpToCard(index),
                    }))}
                    action={
                        <>
                            {!readonly && (
                                <PoolButton
                                    variant="outlined"
                                    color="inherit"
                                    size="small"
                                    startIcon={<AddIcon />}
                                    onClick={this.handleCreateCustomer}
                                >
                                    <FormattedMessage id="app.inquiries.detail.addCustomer" />
                                </PoolButton>
                            )}
                            {/* One switch for the tables of every card (#2257). */}
                            <Box sx={{ marginLeft: 'auto' }}>
                                <PaloDescriptionModeToggle />
                            </Box>
                        </>
                    }
                />
                {accountPurchases.map((accountPurchase, index) =>
                    this.renderCustomer(formikBag, readonly, accountPurchase, index)
                )}
            </>
        );
    }

    private renderCustomer(
        formikBag: FormikProps<InquiryDetailPositionsFormValues>,
        readonly: boolean,
        accountPurchase: AccountPurchase,
        index: number
    ) {
        const prefix = `accountPurchases.${index}`;
        const noCustomer = !accountPurchase.customerAccount;
        const positions = accountPurchase.purchaseItems?.length ?? 0;

        return (
            <PaloRecordCard
                key={index}
                id={`inquiry-customer-${index}`}
                title={
                    <>
                        <FormattedMessage id="app.header.customerWithValue" values={{ value: index + 1 }} />
                        {customerLabel(accountPurchase) ? ` · ${customerLabel(accountPurchase)}` : ''}
                    </>
                }
                meta={<FormattedMessage id="app.inquiries.detail.positionCount" values={{ count: positions }} />}
                headerControl={
                    <PaloDeliveryDateControl
                        typeName={`${prefix}.deliveryDateType`}
                        dateName={`${prefix}.deliveryDate`}
                        readonly={readonly}
                        withError
                    />
                }
                actions={
                    !readonly && formikBag.values.accountPurchases.length > 1 ? (
                        <IconButton size="small" onClick={this.handleRemoveCustomer(index)}>
                            <DeclineIcon />
                        </IconButton>
                    ) : undefined
                }
                open={this.isCardOpen(formikBag, index)}
                onToggle={this.toggleCard(formikBag, index)}
                faulty={formikBag.submitCount > 0 && !!getIn(formikBag.errors, `accountPurchases.${index}`)}
            >
                <PaloFormGrid
                    columns={4}
                    labels="side"
                    areas={CUSTOMER_CARD_AREAS}
                    narrowAreas={CUSTOMER_CARD_NARROW_AREAS}
                >
                    <PaloFormikField<Customer>
                        name={`${prefix}.customerAccount`}
                        label={<FormattedMessage id="app.label.customer" />}
                        required
                        area="customer"
                    >
                        {({ field, hasError }) => (
                            <PaloCustomerQuickSearch
                                id={`inquiry-quick-search-${index}`}
                                picked={!noCustomer}
                                readonly={readonly}
                                onSelected={this.handleQuickSearchSelected(formikBag, prefix)}
                                onePerAddress
                            >
                                {searchButton => (
                                    <PoolCustomerAutocompleteList
                                        label={<></>}
                                        required={false}
                                        onSelected={this.handleCustomerSelected(formikBag, prefix)}
                                        initialValue={this.customerName}
                                        error={hasError}
                                        readOnly={readonly}
                                        {...field}
                                        adornment={searchButton}
                                        isLocked={false}
                                    />
                                )}
                            </PaloCustomerQuickSearch>
                        )}
                    </PaloFormikField>
                    <PaloFormikField<PartialAddress>
                        name={`${prefix}.deliveryAddress`}
                        label={<FormattedMessage id="app.label.deliveryAddress" />}
                        required
                        area="deliveryAddress"
                    >
                        {({ field, hasError }) => (
                            <PoolAccountAddressAutocompleteList
                                accountId={this.getCurrentCustomerId(formikBag, index)}
                                autocompleteType="DeliveryAddress"
                                label={<></>}
                                required={false}
                                disabled={noCustomer}
                                error={hasError}
                                readOnly={readonly}
                                onSelected={this.handleDeliveryAddressSelected(formikBag, prefix)}
                                {...field}
                            />
                        )}
                    </PaloFormikField>
                    <PaloFormikField<PartialAccountContact>
                        name={`${prefix}.contact`}
                        label={<FormattedMessage id="app.table.column.contact" />}
                        required
                        area="contact"
                    >
                        {({ field, hasError }) => (
                            <PoolAccountContactAutocompleteList
                                accountId={this.getCurrentCustomerId(formikBag, index)}
                                label={<></>}
                                required={false}
                                disabled={noCustomer}
                                onSelected={this.setFieldValue(formikBag, field.name)}
                                error={hasError}
                                readOnly={readonly}
                                {...field}
                            />
                        )}
                    </PaloFormikField>
                    <PaloFormikField<PartialAccountContact>
                        name={`${prefix}.avisoContact`}
                        label={<FormattedMessage id="app.label.avisoContact" />}
                        area="aviso"
                    >
                        {({ field, hasError }) => (
                            <PoolAccountContactAutocompleteList
                                accountId={this.getCurrentCustomerId(formikBag, index)}
                                label={<></>}
                                disabled={noCustomer}
                                onSelected={this.setFieldValue(formikBag, field.name)}
                                error={hasError}
                                readOnly={readonly}
                                {...field}
                            />
                        )}
                    </PaloFormikField>
                    <PaloTextControl
                        name={`${prefix}.costCentre`}
                        label={<FormattedMessage id="app.label.costCentre" />}
                        area="costCentre"
                        disabled={noCustomer || readonly}
                    />
                    <PaloTextControl
                        name={`${prefix}.orderReference`}
                        label={<FormattedMessage id="app.label.reference" />}
                        area="reference"
                        disabled={noCustomer || readonly}
                    />
                </PaloFormGrid>
                <InquiryDetailPositionsArticles
                    accountPurchaseIndex={index}
                    user={this.props.user}
                    locale={this.props.locale}
                    mediaBaseUrl={this.props.mediaBaseUrl}
                    onOpenCustomPosition={this.openAddCustomPositionModal}
                    readonly={readonly}
                />
            </PaloRecordCard>
        );
    }

    public render() {
        const { classes } = this.props;

        const schema = Yup.object().shape({
            accountPurchases: Yup.array().of(
                Yup.lazy(
                    (accountPurchase: AccountPurchase) =>
                        isBlankCard(accountPurchase)
                            ? Yup.object()
                            : Yup.object().shape({
                                  customerAccount: Yup.mixed().required('app.error.required'),
                                  deliveryAddress: Yup.mixed().required('app.error.required'),
                                  contact: Yup.mixed().required('app.error.required'),
                                  deliveryDate: Yup.date()
                                      .required('app.error.required')
                                      .typeError('app.error.required'),
                                  purchaseItems: Yup.array()
                                      .of(
                                          // The blank first row waits for its article and is
                                          // not saved (#2308).
                                          Yup.lazy(
                                              (item: PurchaseItem) =>
                                                  isBlankPosition(item)
                                                      ? Yup.object()
                                                      : Yup.object().shape({
                                                            quantity: Yup.number()
                                                                .moreThan(0, 'app.error.greaterZero')
                                                                .typeError('app.error.required'),
                                                        })
                                              // HACK: type definition of Yup.lazy seems to be wrong
                                              // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                          ) as any
                                      )
                                      .required('app.inquiries.detail.error.selectProduct')
                                      .test(
                                          'one-position',
                                          'app.inquiries.detail.error.selectProduct',
                                          items => 0 < withoutBlankPositions((items ?? []) as PurchaseItem[]).length
                                      ),
                              })
                    // HACK: type definition of Yup.lazy seems to be wrong
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                ) as any
            ),
        });

        const readonly = this.isReadonly();

        return (
            <div className={classes.container}>
                <div className={classes.content}>
                    <div>
                        <Formik
                            innerRef={ref => (this.formikInstance = ref)}
                            initialValues={this.initialValues}
                            onSubmit={this.onSubmit}
                            validationSchema={schema}
                        >
                            {(formikBag: FormikProps<InquiryDetailPositionsFormValues>) => (
                                <Form>
                                    <PaloDirtyComparison baseline={this.toFormValues(this.props.documentState)} />
                                    <ScrollToFieldError />
                                    <PaloInquiryNotesWatcher />
                                    {this.renderCustomers(formikBag, readonly)}
                                </Form>
                            )}
                        </Formik>
                    </div>
                </div>

                <PaloNextThemeProvider>
                    <FreePositionDialog
                        customPosition={this.state.customProduct}
                        customData={this.state.customData}
                        isOpen={this.state.customPositionModalOpen}
                        currencySymbol={this.props.currencySymbol}
                        locale={this.props.appLocale}
                        requiredFields={[CustomPositionFields.OrderUnit, CustomPositionFields.Quantity]}
                        hideFields={[CustomPositionFields.Tax, CustomPositionFields.Price]}
                        onCancel={this.closeAddCustomPositionModal}
                        onSave={this.handleAddCustomPosition}
                    />
                </PaloNextThemeProvider>
            </div>
        );
    }
}

const mapStateToProps = (state: AppState) => {
    const locale = bestLocaleSelector(state);
    return {
        locale,
        appLocale: state.currentLocale,
        currency: state.settings.currency,
        currencySymbol: getCurrencySymbol(state.settings.currency, locale),
        user: state.user,
        mediaBaseUrl: state.settings.mediaBaseUrl,
    };
};

const ConnectedInquiryDetailPositions = connect(mapStateToProps, {})(InquiryDetailPositionsBase);

export const InquiryDetailPositions = withStyles(styles)(ConnectedInquiryDetailPositions);
